import React, {useEffect, useState} from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {Checkbox} from "@material-ui/core";
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';
import {
  EnhancedTable,
  AlertDialog,
  DraggableDialog,
  PromptDialog
} from '../components';
import { PythonExecutable, trackToolUsage } from '../executable';
import { csv, getTempFilePath, ipcRenderer} from '../electron-modules';
import {
  saveMatrixFile,
  saveProject
} from "../utilities";
import { Box, DialogContentText } from '@material-ui/core';
import clsx from 'clsx';
import path from 'path';
import {
  changeBasicAdjustments,
  changeCameraTransform,
  selectBasicAdjustments,
  selectCameraTransform,
  selectActiveCamera
} from '../redux/camera-slice';
import { selectAllAssets, getCameraFileAssets } from '../redux/assets-slice';
import { Viewer } from '../viewer/js/main';
import { pointObsColor, imageObsColor } from '../viewer/js/image-aligner';
import LocalScene, { SceneCoordinate } from '../viewer/js/projections';
import { toast } from '../app';
import { useTranslation } from 'react-i18next';

const highCutoff = 1.0;
const lowCutoff = 0.5;
const requiredRows = 5;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    minHeightContent: {
      minHeight: "100px"
    },
    toggleOptionParent: {
      margin: theme.spacing(0),
      padding: theme.spacing(0),
      boxShadow: "0px 10px 10px 2px",
      justifyContent: "center"
    },
    toggleOptionCheckbox: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5)
    },
    toggleOptionFlex: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    userWarning: {
      padding: theme.spacing(3)
    },
    box: {
      padding: theme.spacing(4)
    },
    marginTop: {
      marginTop: theme.spacing(2)
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    adornedEnd: {
      paddingRight: theme.spacing(0)
    },
    utmButton: {
      marginLeft: theme.spacing(1)
    },
    selectMargin: {
      margin: theme.spacing(1),
    },
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1)
    },
    dropdown: {
      minWidth: 200,
    },
    formControl: {
      width: '100%'
    },
    buttonDiv: {
      textAlign: "right"
    },
    success: {
      color: theme.palette.success.main
    },
    imageObservationSuccess: {
      color: `#${imageObsColor.toString(16)}`
    },
    pointObservationSuccess: {
      color: `#${pointObsColor.toString(16)}`
    },
    warning: {
      color: theme.palette.warning.main
    },
    error: {
      color: theme.palette.error.main
    },
    info: {
      color: theme.palette.info.main
    },
    errorLow: {
      color: theme.palette.success.main
    },
    errorMedium: {
      color: theme.palette.warning.main
    },
    errorHigh: {
      color: theme.palette.error.main
    },
    assetCheckbox: {
      position: "relative",
      transform: "translateY(0%)"
    },
    checkboxPadding: {
      padding: "0px"
    }
  }),
);

const PointObservationIcon = (props) => {
  return ObservationIcon(props, 'point');
};

const ImageObservationIcon = (props) => {
  return ObservationIcon(props, 'image');
};


const ObservationIcon = (props, type) => {
  const {color} = props;
  const classes = useStyles();

  return (
    <IconButton
      size="small"
      classes={{
        colorPrimary: type === 'image' ? classes.imageObservationSuccess : classes.pointObservationSuccess,
        colorSecondary: classes.error,
        colorInherit: classes.info,
      }}
      color={color}
    >
      <ControlPointIcon fontSize="small"/>
    </IconButton>
  );
};

const ResidualError = (props) => {
  const {value} = props;
  const classes = useStyles();
  return (
    <Box className={clsx("", {
      [classes.errorLow]: (value < lowCutoff),
      [classes.errorMedium]: (value < highCutoff) && (value >= lowCutoff),
      [classes.errorHigh]: (value >= highCutoff)
    })}>
      {value}
    </Box>
  );
};

export const ImageAlignerAdvanced = (props) => {
  const viewer = props.viewer as Viewer;
  const {addObservations, setMeasurementsOpen,
    observationType, setObservationType, alignerAdvOpen,
    alignerBasicOpen, setAlignerAdvOpen} = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [csvPath, setCsvPath] = useState("");
  const [encompassCsvPath, setEncompassCsvPath] = useState("");
  const [aligned, setAligned] = useState(false);
  const [alignmentConfirm, setAlignmentConfirm] = useState(false);
  const [alignerMatrixPath, setAlignerMatrixPath] = useState(null);
  const [valid, setValid] = useState(false);
  const [tableNonEmpty, setTableNonEmpty] = useState(false);
  const [closePrompt, setClosePrompt] = useState(false);
  const [isAligned, setIsAligned] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [askRemoveOpen, setAskRemoveOpen] = useState(false);
  const [alignPosition, setAlignPosition] = useState(true);
  const [alignRotation, setAlignRotation] = useState(true);

  const cameraTransformPath = useSelector(selectCameraTransform);
  const currentCamera = useSelector(selectActiveCamera);
  const basicAdjustments = useSelector(selectBasicAdjustments);

  const assets = useSelector(selectAllAssets);
  const camerasFiles = getCameraFileAssets(assets);

  const {t, i18n} = useTranslation();

  useEffect(() => {
    updateRows([...rows]);
  }, [i18n.language]);

  const columns = [
    {
      id: 'obs_num',
      label: '#',
      numeric: true
    },
    {
      id: 'name',
      label: t("data-aligner-adv.filename"),
      pointer: true,
      wordBreak: true
    },
    {
      id: 'img_obs',
      label: t("data-aligner-adv.image-control"),
      center: true
    },
    {
      id: 'pc_obs',
      label: t("data-aligner-adv.point-control"),
      center: true
    },
    {
      id: 'error',
      label: t("data-aligner-adv.error") + '(\u00B0)',
      numeric: true,
      center: true
    }
  ];

  const handleOpen = () => {
    if (alignerBasicOpen) {
      setAlertOpen(true);
      return;
    }

    setAlignerAdvOpen(true);
    setMeasurementsOpen(false);
  };

  const handleClose = () => {
    setAlignerAdvOpen(false);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const resetRowsAndMesh = () => {
    setRows([]);
    generateRows(requiredRows);
  };

  const exportCSV = () => {
    let csvPath = getTempFilePath('alignTemp.csv');
    let csvEncompassPath = getTempFilePath('encompassTemp.csv');

    let csvOut = [];
    csvOut.push(['filename',
      'x_img', 'y_img', 'z_img',
      'x_ground', 'y_ground', 'z_ground']);
    for (let i=0; i<rows.length; i++) {
      let imageObs = viewer.getImageObsCameraAligner(i);
      let pointObs = viewer.getPointObsCameraAligner(i);
      csvOut.push([rows[i].name,
        imageObs.x, imageObs.y, imageObs.z,
        pointObs.x, pointObs.y, pointObs.z]);
    }
    csv.writeToPath(csvPath, csvOut)
      .on('error', err => console.error(err))
      .on('finish', () => console.log('Done writing.'));
    setCsvPath(csvPath);


    let csvEncompassOut = [];
    csvEncompassOut.push(['filename',
      'X', 'Y', 'Z',
      'Roll', 'Pitch', 'Yaw']);
    for (let i=0; i< viewer.getNumEntriesCameraAligner(); i++) {
      csvEncompassOut.push(viewer.getCamPoseCameraAligner(i));
    }
    csv.writeToPath(csvEncompassPath, csvEncompassOut)
      .on('error', err => console.error(err))
      .on('finish', () => console.log('Done writing.'));
    setEncompassCsvPath(csvEncompassPath);
  };

  const projectionUnits = LocalScene.dataProjectionUnits;
  const runAlignerPython =() => {
    let exe = new PythonExecutable();
    let commands = [
      "-p", "camera_aligner_advanced",
      "--csv_obs", csvPath,
      "--csv_encompass", encompassCsvPath,
      "--sulphur", true,
      "--align_position", alignPosition,
      "--align_rotation", alignRotation,
      "--units", projectionUnits
    ];

    let isConverted = false;
    let camPoseEntry = viewer.getCamPoseCameraAligner(0);
    let largeResiduals = false;

    let initialPath = path.dirname(camPoseEntry[0]);
    let nameCorrection = path.basename(initialPath);
    let correctionName = nameCorrection + "_correction_matrix.4x4";
    let correctionPath = path.join(initialPath, correctionName);

    trackToolUsage("aligner-advanced");

    exe.run({
      command: commands,
      saveLogs: true,
      onLine: jsonData => {
        let residualsTemp = parseResiduals(jsonData);
        if (residualsTemp.length !== 0) {
          viewer?.setErrorsCameraAligner(residualsTemp);
          isConverted = true;
        }
        residualsTemp.forEach(res => {
          if (res >= highCutoff) {
            largeResiduals = true;
          }
        });
      },
      onClose: () => {
        if (isConverted) {
          toast.success(t("toast.alignment-completed"));
          setAlignerMatrixPath(correctionPath);
          setAligned(true);
          updateRows(resetRowIcons());

          if (largeResiduals) {
            toast.warning(t("toast.data-aligner-adv-large-error"));
          }
        } else {
          toast.error(t("toast.aligner-failed"));
        }
      }
    });
  };

  const parseResiduals = (jsonData) => {
    let resArray = [];

    if (jsonData) {
      jsonData.forEach(res => {
        let rounded = Math.round(parseFloat(res) * 100) / 100;
        resArray.push(rounded);
      });
    }

    return resArray;
  };

  const emptyObservation = (index) => {
    return {
      id: index,
      obs_num: index+1,
      name: t("data-aligner-adv.no-file"),
      img_obs: <ImageObservationIcon color="secondary"/>,
      pc_obs: <PointObservationIcon color="secondary"/>,
      error: 0
    };
  };

  const generateRows = (rowNum) => {
    viewer?.resetCameraAligner();

    let rowsTemp = [];
    for (let i=0; i<rowNum; i++) {
      rowsTemp.push(emptyObservation(i));
      viewer?.addRowCameraAligner();
    }
    updateRows(rowsTemp);
  };

  const addRows = (rowAdd) => {
    let tempRows = [...rows];

    for (let i=0; i<rowAdd; i++) {
      tempRows.push(emptyObservation(i));
      viewer.addRowCameraAligner();
    }

    updateRows(tempRows);
    toast.success(t("toast.alignment-added"));
    setAlignmentConfirm(false);
    if (aligned) {
      cancelCurrentAlignment();
      setValid(false);
    }
  };

  const updateRows = (tempRows) => {
    if (tempRows.length === 0) {
      return;
    }

    for (let i=0; i<tempRows.length; i++) {
      tempRows[i].id = i;
      tempRows[i].obs_num = i + 1;
      tempRows[i].name = viewer?.getAlignerCameraName(i);
      tempRows[i].error = <ResidualError value={viewer?.getErrorCameraAligner(i)}/>;
    }

    setRows(tempRows);
  };

  const onDeleteClick = (row) => {
    if (rows.length === 1) {
      resetRowsAndMesh();
      return;
    }
    let tempRows = resetRowIcons();
    viewer.deleteRowCameraAligner(row.id);
    tempRows.splice(row.id, 1);
    updateRows(tempRows);
    setObservationType("none");
    toast.error(t("toast.aligner-deleted"));

    setAlignmentConfirm(false);
    if (aligned) {
      cancelCurrentAlignment();
      setValid(false);
    }
  };

  const resetRowIcons = () => {
    let tempRows = [...rows];

    for (let i=0; i<tempRows.length; i++) {
      const hasImageObs = viewer.getImageObsCameraAligner(i) != null;
      tempRows[i].img_obs = <ImageObservationIcon color={hasImageObs ? "primary" : "secondary"}/>;

      const hasPointObs = viewer.getPointObsCameraAligner(i) != null;
      tempRows[i].pc_obs = <PointObservationIcon color={hasPointObs ? "primary" : "secondary"}/>;
    }

    return tempRows;
  };

  const onRowClick = (row, index) => {
    if (index > 3 || index < 2){
      setObservationType("none");
      viewer.setCurrentRowCameraAligner(-1);

      const camID = viewer.getCamIdFromAligner(row.id);
      const position = viewer.getObservationPosition(row.id);

      if (position) {
        const lookat = new SceneCoordinate(position).toDataProjection();
        viewer.setCameraVisible(camID, {lookat});
        viewer.flashObservations(row.id);
      }
    } else if (index === 2){
      if (aligned || viewer?.checkIfAligned()) {
        cancelCurrentAlignment();
        setValid(false);
      }

      let tempRows = resetRowIcons();
      tempRows[row.id].img_obs = <ImageObservationIcon color="inherit" />;

      updateRows(tempRows);

      setObservationType("image");
      viewer.setCurrentRowCameraAligner(row.id);
      viewer.hideCurrentObservation('image', row.id);
      setAlignmentConfirm(false);
    } else if (index === 3) {
      if (aligned || viewer?.checkIfAligned()) {
        cancelCurrentAlignment();
        setValid(false);
      }

      let tempRows = resetRowIcons();

      tempRows[row.id].pc_obs = <PointObservationIcon color="inherit"/>;
      updateRows(tempRows);

      setObservationType("point");
      viewer.setCurrentRowCameraAligner(row.id);
      viewer.hideCurrentObservation('point', row.id);
      setAlignmentConfirm(false);
    }
  };

  const cancelCurrentAlignment = () => {
    setAligned(false);
    setIsAligned(false);
    setAlignmentConfirm(false);
    setAlignerMatrixPath(null);
    dispatch(changeCameraTransform(null));
    toast.success(t("toast.alignment-cleared"));
  };

  const onClearAlignments = () => {
    dispatch(changeBasicAdjustments({}));
    viewer?.resetBasicAlignerCamera();
    saveProject(false);
    cancelCurrentAlignment();
  };

  const confirmAlignment = () => {
    dispatch(changeCameraTransform(alignerMatrixPath));
    setAlignmentConfirm(true);
  };

  const handleAlignPositionCheck = () => {
    setAlignPosition(!alignPosition);
  };

  const handleAlignRotationCheck = () => {
    setAlignRotation(!alignRotation);
  };

  const checkRowsValid = () => {
    if (rows.length < requiredRows) {
      setValid(false);
      setTableNonEmpty(false);
      return false;
    }

    if (!viewer.getNumEntriesCameraAligner()){
      setValid(false);
      return false;
    }

    if (viewer === null) {
      setValid(false);
      setTableNonEmpty(false);
      return false;
    }

    let validCheck = true;
    let nonEmpty = false;
    for (let i=0; i < rows.length; i++) {
      let imgCheck = (viewer?.getImageObsCameraAligner(i) != null);
      let pointCheck = (viewer?.getPointObsCameraAligner(i) != null);
      let validTemp = (imgCheck && pointCheck);
      validCheck = (validCheck && validTemp);
      nonEmpty = (imgCheck || pointCheck || nonEmpty);
    }
    setValid(validCheck);
    setTableNonEmpty(nonEmpty);
    return validCheck;
  };

  const checkClose = () => {
    if (tableNonEmpty) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-image-aligner-adv");
    ipcRenderer?.on("open-image-aligner-adv", handleOpen);
  }, [alignerBasicOpen]);

  useEffect(() => {
    if (viewer) {
      generateRows(requiredRows);
    }
  }, [viewer]);

  useEffect(() => {
    const aligned = cameraTransformPath !== null;
    const removed = currentCamera === null;
    const noCameras = camerasFiles.length === 0;

    if (aligned && removed && noCameras) {
      setAskRemoveOpen(true);
    }
  }, [currentCamera]);

  useEffect(() => {
    if (addObservations) {
      toast.success(t(`toast.alignment-${observationType}-added`));
      updateRows(resetRowIcons());
      setObservationType("none");
      checkRowsValid();
    }
  }, [addObservations]);

  useEffect(() => {
    if (!viewer) {
      return;
    }

    viewer?.setObservationTypeCameraAligner(observationType);
    if (observationType==="none") {
      updateRows(resetRowIcons());
    }
  }, [viewer, observationType]);

  useEffect(() => {
    if (!alignerAdvOpen) {
      resetRowsAndMesh();
    }

    viewer?.toggleCameraAligner(alignerAdvOpen);
  }, [viewer, alignerAdvOpen]);

  useEffect(() => {
    let validCheck = checkRowsValid();
    if (validCheck) {
      exportCSV();
    }
  }, [rows]);

  useEffect(() => {
    setIsAligned(viewer?.checkIfAligned() || hasBasicAlignment);
  }, [viewer, alignerAdvOpen, aligned, isAligned]);

  const hasBasicAlignment = Object.keys(basicAdjustments).length !== 0;
  const submitDisabled = !alignRotation && !alignPosition;

  return (
    <React.Fragment>

      <DraggableDialog
        open={alignerAdvOpen}
        title={t("data-aligner-adv.title")}
        onClose={checkClose}
      >
        <DialogContent className={classes.minHeightContent}>

          {(!isAligned) && (<Typography variant="caption">
            {t("data-aligner-adv.select-at-least", {requiredRows: requiredRows})}
          </Typography>)}

          {(isAligned) && (<Typography variant="caption" className={classes.warning}>
            {t("data-aligner-adv.have-set-alignment")}
          </Typography>)}

          {(!isAligned) && <EnhancedTable
            dense
            deletable
            sortable={false}
            columns={columns}
            rows={rows}
            className={classes.marginTop}
            rowType={t("data-aligner-adv.control-point")}
            onRowClick={onRowClick}
            onDeleteClick={onDeleteClick}
            pageSize={10}
          />}
        </DialogContent>

        <DialogActions>
          {rows.length === 0 && (
            <Button onClick= {() => generateRows(requiredRows)} color="primary">
              {t("data-aligner-adv.select-control-points")}
            </Button>
          )}

          {(!isAligned) && (rows.length >= 1) &&
            ((alignmentConfirm === false)) && (
            <Button onClick= {() => addRows(1)} color="inherit" >
              {t("data-aligner-adv.add-entry")}
            </Button>
          )}

          {(valid === true) && (aligned === false) && (
            <Button
              onClick={runAlignerPython}
              disabled={submitDisabled}
              color="primary"
            >
              {t("data-aligner-adv.run-alignment-correction")}
            </Button>
          )}

          {(aligned === true) && (alignmentConfirm === true) && (
            <Button onClick={()=>saveMatrixFile(cameraTransformPath)} color="primary">
              {t("data-aligner-adv.save-camera-matrix")}
            </Button>
          )}

          {(aligned === true) && (
            <Button onClick={cancelCurrentAlignment} color="secondary">
              {t("data-aligner-adv.cancel-current-alignment")}
            </Button>
          )}

          {(aligned === false) && (isAligned === true) && (
            <Button onClick={onClearAlignments} color="secondary">
              {t("data-aligner-adv.clear-alignment")}
            </Button>
          )}

          {(aligned === true) && (alignmentConfirm === false) && (
            <Button onClick={confirmAlignment} color="primary">
              {t("data-aligner-adv.apply-alignment-correction")}
            </Button>
          )}

        </DialogActions>

        {!isAligned && (<DialogActions className={classes.toggleOptionParent}>

          {/* Calculate lever arm */}
          <div className={classes.toggleOptionFlex}>
            <Typography variant="caption">
              {t("data-aligner-adv.align-position")}
            </Typography>
            <Checkbox
              checked={alignPosition}
              onChange={handleAlignPositionCheck}
              classes={{root: classes.toggleOptionCheckbox}}
              size="small"
            />
          </div>

          {/* Calculate rotation */}
          <div className={classes.toggleOptionFlex}>
            <Typography variant="caption">
              {t("data-aligner-adv.align-rotation")}
            </Typography>
            <Checkbox
              checked={alignRotation}
              onChange={handleAlignRotationCheck}
              classes={{root: classes.toggleOptionCheckbox}}
              size="small"
            />
          </div>

        </DialogActions>)}
      </DraggableDialog>

      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          handleClose();
          setClosePrompt(false);
        }}
        title={t("data-aligner-adv.prompt-close-aligner-title")}
        prompt={t("data-aligner-adv.prompt-close-aligner-text")}
        button={t('buttons.close')}
      />

      <PromptDialog
        open={askRemoveOpen}
        onCancel={() => setAskRemoveOpen(false)}
        onSubmit={() => {
          dispatch(changeCameraTransform(null));
          setAskRemoveOpen(false);
        }}
        title={t("data-aligner-adv.prompt-remove-matrix-title")}
        prompt={t("data-aligner-adv.prompt-remove-matrix-text")}
        button={t('buttons.remove-matrix')}
      />

      <AlertDialog
        open={alertOpen}
        title={t("data-aligner-adv.title")}
        handleClose={handleAlertClose}
      >
        <DialogContentText>
          {t("data-aligner-adv.unable-to-open")}
        </DialogContentText>
      </AlertDialog>
    </React.Fragment>
  );
};
